import type { BreachTableRow } from "@/views/Risk/Breaches/utils";
import type { RiskTableRow } from "@/views/Risk/Risks/risksTableConfig";
import type { SignalTableRow } from "@/views/Risk/Signals/utils";
import type { TaskTableRow } from "@/views/Risk/Tasks/utils";

export type CountRow = {
  name: string;
  values: Record<string, number>;
};

export type RiskAnalyticsData = {
  totalRisks: number;
  breachedSignals: number;
  activeIssues: number;
  overdueTasks: number;
  avgRiskScore: string;
  byRiskCategory: CountRow[];
  bySignalHealth: CountRow[];
  byIssueStatus: CountRow[];
  byTaskPriority: CountRow[];
  byRiskScoreBand: CountRow[];
  byEscalationLevel: CountRow[];
  byOwnerLoad: CountRow[];
};

const CLOSED_TASK_STATUSES = new Set(["Closed", "Resolved", "Done"]);
const RESOLVED_ISSUE_STATUSES = new Set(["Resolved", "Closed"]);

const getStringValue = (
  value: string | number | boolean | null | undefined,
): string | null => {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const countBy = (
  rows: Array<Record<string, string | number | boolean | null>>,
  key: string,
  formatter?: (value: string | null) => string,
) => {
  const counts = new Map<string, number>();

  rows.forEach((row) => {
    const rawValue = getStringValue(row[key]);
    const label = formatter ? formatter(rawValue) : rawValue || "Unspecified";
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  return counts;
};

const toCountRows = (
  counts: Map<string, number>,
  valueLabel: string,
  options?: { limit?: number; sort?: boolean },
): CountRow[] => {
  const entries = Array.from(counts.entries());
  const orderedEntries =
    options?.sort === false
      ? entries
      : [...entries].sort((left, right) => right[1] - left[1]);
  const rows = orderedEntries.map(([name, value]) => ({
    name,
    values: { [valueLabel]: value },
  }));

  return typeof options?.limit === "number"
    ? rows.slice(0, options.limit)
    : rows;
};

const getAverageRiskScore = (rows: RiskTableRow[]) => {
  const scores = rows
    .map((row) => Number(row.assessed_risk_score))
    .filter((value) => !Number.isNaN(value));

  if (scores.length === 0) return 0;

  return scores.reduce((sum, value) => sum + value, 0) / scores.length;
};

const getRiskScoreBand = (value: number) => {
  if (value >= 60) return "60+";
  if (value >= 40) return "40-59";
  if (value >= 20) return "20-39";
  return "0-19";
};

const createRiskScoreBands = (rows: RiskTableRow[]) => {
  const counts = new Map<string, number>([
    ["0-19", 0],
    ["20-39", 0],
    ["40-59", 0],
    ["60+", 0],
  ]);

  rows.forEach((row) => {
    const score = Number(row.assessed_risk_score);
    if (Number.isNaN(score)) return;

    const band = getRiskScoreBand(score);
    counts.set(band, (counts.get(band) || 0) + 1);
  });

  return counts;
};

const createOwnerLoad = (
  risksData: RiskTableRow[],
  breachesData: BreachTableRow[],
  tasksData: TaskTableRow[],
) => {
  const owners = new Map<string, Record<string, number>>();

  const increment = (
    name: string | null,
    type: "Risks" | "Issues" | "Tasks",
  ) => {
    const key = name || "Unassigned";
    const current = owners.get(key) || { Risks: 0, Issues: 0, Tasks: 0 };
    current[type] += 1;
    owners.set(key, current);
  };

  risksData.forEach((row) => increment(getStringValue(row.assignee), "Risks"));
  breachesData.forEach((row) =>
    increment(getStringValue(row.assigned_to), "Issues"),
  );
  tasksData.forEach((row) =>
    increment(getStringValue(row.assigned_role), "Tasks"),
  );

  return Array.from(owners.entries())
    .map(([name, values]) => ({ name, values }))
    .sort(
      (left, right) =>
        right.values.Risks +
        right.values.Issues +
        right.values.Tasks -
        (left.values.Risks + left.values.Issues + left.values.Tasks),
    );
};

export const buildRiskAnalytics = (
  risksData: RiskTableRow[],
  signalsData: SignalTableRow[],
  breachesData: BreachTableRow[],
  tasksData: TaskTableRow[],
): RiskAnalyticsData => {
  const today = new Date();
  const activeIssues = breachesData.filter((row) => {
    const status = getStringValue(row.status);
    return !status || !RESOLVED_ISSUE_STATUSES.has(status);
  }).length;
  const overdueTasks = tasksData.filter((row) => {
    const dueDate = getStringValue(row.due_date);
    const status = getStringValue(row.status);

    if (!dueDate || (status && CLOSED_TASK_STATUSES.has(status))) return false;

    return dueDate < today.toISOString().slice(0, 10);
  }).length;

  return {
    totalRisks: risksData.length,
    breachedSignals: signalsData.filter((row) => row.breach_status === true)
      .length,
    activeIssues,
    overdueTasks,
    avgRiskScore: getAverageRiskScore(risksData).toFixed(1),
    byRiskCategory: toCountRows(countBy(risksData, "category"), "Risks", {
      limit: 6,
    }),
    bySignalHealth: toCountRows(
      (() => {
        const counts = new Map<string, number>([
          ["Green", 0],
          ["Yellow", 0],
          ["Red", 0],
        ]);
        signalsData.forEach((row) => {
          const label = getStringValue(row.band) || "Unspecified";
          counts.set(label, (counts.get(label) || 0) + 1);
        });
        return counts;
      })(),
      "Signals",
      { sort: false },
    ),
    byIssueStatus: toCountRows(countBy(breachesData, "status"), "Issues"),
    byTaskPriority: toCountRows(
      (() => {
        const counts = new Map<string, number>([
          ["High", 0],
          ["Medium", 0],
          ["Low", 0],
        ]);
        tasksData.forEach((row) => {
          const label = getStringValue(row.priority) || "Unspecified";
          counts.set(label, (counts.get(label) || 0) + 1);
        });
        return counts;
      })(),
      "Tasks",
      { sort: false },
    ),
    byRiskScoreBand: toCountRows(createRiskScoreBands(risksData), "Risks", {
      sort: false,
    }),
    byEscalationLevel: toCountRows(
      countBy(breachesData, "escalation_level"),
      "Issues",
    ),
    byOwnerLoad: createOwnerLoad(risksData, breachesData, tasksData),
  };
};
